/**
 * Stock Card queries — PRD.193.
 *
 * The Stock Card is a READ-ONLY ledger view over `stock_movements`. This
 * module exposes only queries — no helpers that write or mutate the
 * ledger. The single source of truth is `stock_movements`; everything
 * here joins it back to item/batch/source metadata for human-readable
 * display.
 *
 * Running balance is intentionally computed in JS (`computeRunningBalances`
 * below) rather than via a SQL window function, to stay portable across
 * the PowerSync SQLite client and keep the QuerySpec stable.
 */

import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Joined row returned by the Stock Card query ───

/**
 * One row per stock movement, joined with item, batch, and source
 * metadata. Source-specific reference fields are populated only for the
 * matching `source_type` — others are null.
 */
export interface StockCardMovementRow {
  // Movement
  movement_id: string;
  org_id: string | null;
  inventory_item_id: string | null;
  inventory_batch_id: string | null;
  source_type: string | null;
  source_id: string | null;
  source_line_id: string | null;
  movement_type: string | null;
  quantity_in: number | null;
  quantity_out: number | null;
  unit: string | null;
  movement_date: string | null;
  remarks: string | null;
  created_by: string | null;
  created_at: string | null;

  // Item join
  item_name: string | null;
  sku: string | null;

  // Batch join
  batch_number: string | null;
  expiry_date: string | null;

  // Source join — exactly one of these is populated per row.
  adjustment_number: string | null;
  do_number: string | null;
  supplier_invoice_number: string | null;
  // PRD.194 — pharmacy dispense source resolution
  dispense_number: string | null;
  // PRD.206 — product release source resolution
  release_number: string | null;

  // PRD.208 — extra joined context for source-aware reference labels.
  // Pharmacy Dispense:
  ph_patient_id: string | null;
  ph_encounter_id: string | null;
  ph_invoice_id: string | null;
  ph_dispensed_at: string | null;
  // Product Release:
  pr_patient_id: string | null;
  pr_encounter_id: string | null;
  pr_invoice_id: string | null;
  pr_released_at: string | null;
  // Resolved invoice (Pharmacy Dispense or Product Release):
  invoice_no: string | null;
  invoice_encounter_id: string | null;
  invoice_patient_id: string | null;
  // Patient name resolved from the dispense/release patient_id (falls
  // back to the invoice's snapshot when patient row is missing).
  patient_name: string | null;
  // Delivery Order supplier display:
  do_supplier_name: string | null;
}

/**
 * View-model shape with resolved source label, reference number, and
 * a per-item cumulative running balance. Produced by
 * `computeRunningBalances` from `StockCardMovementRow[]`.
 */
export interface StockCardMovementVM extends StockCardMovementRow {
  source_label: string;
  reference_no: string | null;
  running_balance: number;
}

// ─── Queries ───

/**
 * All stock movements with item / batch / source metadata joined.
 *
 * Uses LEFT JOINs so a movement still renders even when its referenced
 * row hasn't synced yet (or was deleted in a future PRD). Ordering is
 * newest-first for display, but `computeRunningBalances` re-sorts
 * ascending internally so the cumulative balance is deterministic.
 */
export function qStockCardMovements(): QuerySpec<[]> {
  return {
    key: ['stock-card-movements'] as const,
    sql: `
      SELECT
        sm.id                          AS movement_id,
        sm.org_id                      AS org_id,
        sm.inventory_item_id           AS inventory_item_id,
        sm.inventory_batch_id          AS inventory_batch_id,
        sm.source_type                 AS source_type,
        sm.source_id                   AS source_id,
        sm.source_line_id              AS source_line_id,
        sm.movement_type               AS movement_type,
        sm.quantity_in                 AS quantity_in,
        sm.quantity_out                AS quantity_out,
        sm.unit                        AS unit,
        sm.movement_date               AS movement_date,
        sm.remarks                     AS remarks,
        sm.created_by                  AS created_by,
        sm.created_at                  AS created_at,
        i.item_name                    AS item_name,
        i.sku                          AS sku,
        b.batch_number                 AS batch_number,
        b.expiry_date                  AS expiry_date,
        adj.adjustment_number          AS adjustment_number,
        do_h.do_number                 AS do_number,
        do_h.supplier_invoice_number   AS supplier_invoice_number,
        do_h.supplier_name             AS do_supplier_name,
        ph.dispense_number             AS dispense_number,
        ph.patient_id                  AS ph_patient_id,
        ph.encounter_id                AS ph_encounter_id,
        ph.invoice_id                  AS ph_invoice_id,
        ph.dispensed_at                AS ph_dispensed_at,
        pr.release_number              AS release_number,
        pr.patient_id                  AS pr_patient_id,
        pr.encounter_id                AS pr_encounter_id,
        pr.invoice_id                  AS pr_invoice_id,
        pr.released_at                 AS pr_released_at,
        inv.invoice_no                 AS invoice_no,
        inv.encounter_id               AS invoice_encounter_id,
        inv.patient_id                 AS invoice_patient_id,
        COALESCE(NULLIF(TRIM(pat.full_name), ''), NULLIF(TRIM(inv.patient_name), '')) AS patient_name
      FROM stock_movements sm
      LEFT JOIN inventory_items i
        ON i.id = sm.inventory_item_id
      LEFT JOIN inventory_batches b
        ON b.id = sm.inventory_batch_id
      LEFT JOIN inventory_adjustments adj
        ON adj.id = sm.source_id AND sm.source_type = 'inventory_adjustment'
      LEFT JOIN delivery_orders do_h
        ON do_h.id = sm.source_id AND sm.source_type = 'delivery_order'
      LEFT JOIN pharmacy_dispenses ph
        ON ph.id = sm.source_id AND sm.source_type = 'pharmacy_dispense'
      LEFT JOIN product_releases pr
        ON pr.id = sm.source_id AND sm.source_type = 'product_release'
      -- PRD.208 — resolve invoice (and patient) for Pharmacy Dispense and
      -- Product Release rows so the Reference column can show
      -- "Patient · Invoice · Date" instead of only the raw doc number.
      LEFT JOIN invoices inv
        ON inv.id = COALESCE(ph.invoice_id, pr.invoice_id)
      LEFT JOIN patients pat
        ON pat.id = COALESCE(ph.patient_id, pr.patient_id, inv.patient_id)
      ORDER BY sm.movement_date DESC, sm.created_at DESC, sm.id DESC
    `,
    params: [],
    watch: [
      'stock_movements',
      'inventory_items',
      'inventory_batches',
      'inventory_adjustments',
      'delivery_orders',
      'pharmacy_dispenses',
      'product_releases',
      // PRD.208 — invoice / patient joins drive the human-readable
      // reference label for Pharmacy Dispense and Product Release rows.
      'invoices',
      'patients',
    ],
  };
}

/**
 * Same join, scoped to a single inventory item. Useful for the Stock
 * Card summary view and per-item running balance.
 */
export function qStockCardMovementsByItem(itemId: string): QuerySpec<[string]> {
  return {
    key: ['stock-card-movements-by-item', itemId] as const,
    sql: `
      SELECT
        sm.id                          AS movement_id,
        sm.org_id                      AS org_id,
        sm.inventory_item_id           AS inventory_item_id,
        sm.inventory_batch_id          AS inventory_batch_id,
        sm.source_type                 AS source_type,
        sm.source_id                   AS source_id,
        sm.source_line_id              AS source_line_id,
        sm.movement_type               AS movement_type,
        sm.quantity_in                 AS quantity_in,
        sm.quantity_out                AS quantity_out,
        sm.unit                        AS unit,
        sm.movement_date               AS movement_date,
        sm.remarks                     AS remarks,
        sm.created_by                  AS created_by,
        sm.created_at                  AS created_at,
        i.item_name                    AS item_name,
        i.sku                          AS sku,
        b.batch_number                 AS batch_number,
        b.expiry_date                  AS expiry_date,
        adj.adjustment_number          AS adjustment_number,
        do_h.do_number                 AS do_number,
        do_h.supplier_invoice_number   AS supplier_invoice_number,
        do_h.supplier_name             AS do_supplier_name,
        ph.dispense_number             AS dispense_number,
        ph.patient_id                  AS ph_patient_id,
        ph.encounter_id                AS ph_encounter_id,
        ph.invoice_id                  AS ph_invoice_id,
        ph.dispensed_at                AS ph_dispensed_at,
        pr.release_number              AS release_number,
        pr.patient_id                  AS pr_patient_id,
        pr.encounter_id                AS pr_encounter_id,
        pr.invoice_id                  AS pr_invoice_id,
        pr.released_at                 AS pr_released_at,
        inv.invoice_no                 AS invoice_no,
        inv.encounter_id               AS invoice_encounter_id,
        inv.patient_id                 AS invoice_patient_id,
        COALESCE(NULLIF(TRIM(pat.full_name), ''), NULLIF(TRIM(inv.patient_name), '')) AS patient_name
      FROM stock_movements sm
      LEFT JOIN inventory_items i
        ON i.id = sm.inventory_item_id
      LEFT JOIN inventory_batches b
        ON b.id = sm.inventory_batch_id
      LEFT JOIN inventory_adjustments adj
        ON adj.id = sm.source_id AND sm.source_type = 'inventory_adjustment'
      LEFT JOIN delivery_orders do_h
        ON do_h.id = sm.source_id AND sm.source_type = 'delivery_order'
      LEFT JOIN pharmacy_dispenses ph
        ON ph.id = sm.source_id AND sm.source_type = 'pharmacy_dispense'
      LEFT JOIN product_releases pr
        ON pr.id = sm.source_id AND sm.source_type = 'product_release'
      -- PRD.208 — resolve invoice (and patient) for Pharmacy Dispense and
      -- Product Release rows so the Reference column can show
      -- "Patient · Invoice · Date" instead of only the raw doc number.
      LEFT JOIN invoices inv
        ON inv.id = COALESCE(ph.invoice_id, pr.invoice_id)
      LEFT JOIN patients pat
        ON pat.id = COALESCE(ph.patient_id, pr.patient_id, inv.patient_id)
      WHERE sm.inventory_item_id = ?
      ORDER BY sm.movement_date DESC, sm.created_at DESC, sm.id DESC
    `,
    params: [itemId],
    watch: [
      'stock_movements',
      'inventory_items',
      'inventory_batches',
      'inventory_adjustments',
      'delivery_orders',
      'pharmacy_dispenses',
      'product_releases',
      // PRD.208 — invoice / patient joins drive the human-readable
      // reference label for Pharmacy Dispense and Product Release rows.
      'invoices',
      'patients',
    ],
  };
}

export interface StockCardItemSummaryRow {
  inventory_item_id: string;
  total_in: number;
  total_out: number;
  current_stock: number;
}

/**
 * Aggregate totals for a single item: in / out / current stock.
 * Returns one row even when there are no movements (with zeros).
 */
export function qStockCardSummaryByItem(itemId: string): QuerySpec<[string]> {
  return {
    key: ['stock-card-summary-by-item', itemId] as const,
    sql: `
      SELECT
        ? AS inventory_item_id,
        COALESCE(SUM(COALESCE(quantity_in, 0)), 0)  AS total_in,
        COALESCE(SUM(COALESCE(quantity_out, 0)), 0) AS total_out,
        COALESCE(SUM(COALESCE(quantity_in, 0)), 0)
          - COALESCE(SUM(COALESCE(quantity_out, 0)), 0) AS current_stock
      FROM stock_movements
      WHERE inventory_item_id = ?
    `,
    params: [itemId],
    watch: ['stock_movements'],
  };
}

export interface StockCardBatchOptionRow {
  inventory_batch_id: string | null;
  batch_number: string | null;
  expiry_date: string | null;
}

/**
 * Distinct batches that this item has ever moved through. Drives the
 * batch filter dropdown on the Stock Card page. Includes a `null` row
 * if any movements lack a batch (legacy / non-Medicine).
 */
export function qStockCardBatchesByItem(itemId: string): QuerySpec<[string]> {
  return {
    key: ['stock-card-batches-by-item', itemId] as const,
    sql: `
      SELECT DISTINCT
        sm.inventory_batch_id  AS inventory_batch_id,
        b.batch_number         AS batch_number,
        b.expiry_date          AS expiry_date
      FROM stock_movements sm
      LEFT JOIN inventory_batches b ON b.id = sm.inventory_batch_id
      WHERE sm.inventory_item_id = ?
      ORDER BY
        CASE WHEN b.expiry_date IS NULL THEN 1 ELSE 0 END,
        b.expiry_date ASC,
        b.batch_number ASC
    `,
    params: [itemId],
    watch: ['stock_movements', 'inventory_batches'],
  };
}

// ─── View-model helpers ───

/**
 * PRD.208 — Source-aware reference resolution for the Stock Card
 * Reference column. Returns the human-readable label, an optional
 * secondary technical label (for muted text under the primary line),
 * and a navigation href when a safe in-app route can be derived.
 *
 * Display priorities (per PRD.208 §6):
 *   pharmacy_dispense:
 *     primary   = `Patient · Invoice · Date`
 *                  (falls back through patient+date / invoice+date /
 *                   dispense_number / source_id)
 *     secondary = dispense_number (when shown alongside a non-numeric
 *                 primary; suppressed when primary already ends with
 *                 the same number)
 *     href      = /patient/:patientId/encounters/:encounterId?tab=pharmacy
 *                 → falls back to /patient/:patientId/pharmacy
 *
 *   product_release:
 *     primary   = `Patient · Invoice · Date`
 *     secondary = release_number
 *     href      = /patient/:patientId/encounters/:encounterId?tab=invoices
 *                 → falls back to /patient/:patientId/pharmacy (existing
 *                   patient-wide route; the dedicated patient product
 *                   release page is deferred per PRD.208)
 *
 *   delivery_order:
 *     primary   = do_number
 *     secondary = supplier name and/or supplier_invoice_number
 *     href      = null (no DO detail route exists yet)
 *
 *   inventory_adjustment:
 *     primary   = adjustment_number
 *     secondary = movement_date when present
 *     href      = null (no adjustment detail route exists yet)
 */
export interface StockCardReferenceVM {
  primary_label: string;
  secondary_label: string | null;
  href: string | null;
}

function fmtDateShort(iso: string | null): string | null {
  if (!iso) return null;
  // Accept either YYYY-MM-DD or full ISO; render as "Jan 5".
  const d = new Date(iso.length >= 10 ? iso.slice(0, 10) : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function deriveStockCardReference(row: StockCardMovementRow): StockCardReferenceVM {
  switch (row.source_type) {
    case 'pharmacy_dispense': {
      const date = fmtDateShort(row.ph_dispensed_at ?? row.movement_date);
      const parts = [row.patient_name, row.invoice_no, date].filter(Boolean) as string[];
      const primary =
        parts.length > 0
          ? parts.join(' · ')
          : (row.dispense_number ?? row.source_id ?? '—');
      const secondary =
        parts.length > 0 && row.dispense_number ? row.dispense_number : null;
      const patientId = row.ph_patient_id ?? row.invoice_patient_id;
      const encounterId = row.ph_encounter_id ?? row.invoice_encounter_id;
      const href =
        patientId && encounterId
          ? `/patient/${patientId}/encounters/${encounterId}?tab=pharmacy`
          : patientId
            ? `/patient/${patientId}/pharmacy`
            : null;
      return { primary_label: primary, secondary_label: secondary, href };
    }
    case 'product_release': {
      const date = fmtDateShort(row.pr_released_at ?? row.movement_date);
      const parts = [row.patient_name, row.invoice_no, date].filter(Boolean) as string[];
      const primary =
        parts.length > 0
          ? parts.join(' · ')
          : (row.release_number ?? row.source_id ?? '—');
      const secondary =
        parts.length > 0 && row.release_number ? row.release_number : null;
      const patientId = row.pr_patient_id ?? row.invoice_patient_id;
      const encounterId = row.pr_encounter_id ?? row.invoice_encounter_id;
      const href =
        patientId && encounterId
          ? `/patient/${patientId}/encounters/${encounterId}?tab=invoices`
          : patientId
            ? // Patient-wide pharmacy is the only existing patient-level
              // dispense/release surface today; PRD.208 explicitly allows
              // this fallback until a dedicated patient product release
              // page is added.
              `/patient/${patientId}/pharmacy`
            : null;
      return { primary_label: primary, secondary_label: secondary, href };
    }
    case 'delivery_order': {
      const primary = row.do_number ?? row.supplier_invoice_number ?? row.source_id ?? '—';
      const supplierBits = [row.do_supplier_name, row.supplier_invoice_number]
        .filter(Boolean)
        .filter((v) => v !== row.do_number);
      const secondary = supplierBits.length > 0 ? supplierBits.join(' · ') : null;
      // No DO detail route yet — keep as plain text.
      return { primary_label: primary, secondary_label: secondary, href: null };
    }
    case 'inventory_adjustment': {
      const primary = row.adjustment_number ?? row.source_id ?? '—';
      // Adjustment date is already shown in the Date column, so don't
      // duplicate it in secondary text — keep it null.
      return { primary_label: primary, secondary_label: null, href: null };
    }
    default:
      return {
        primary_label: row.source_id ?? '—',
        secondary_label: row.source_type ?? null,
        href: null,
      };
  }
}

/**
 * Resolve the source label and reference number for a single row, using
 * the same rules documented in PRD.193 §10.
 */
export function resolveSource(row: StockCardMovementRow): {
  source_label: string;
  reference_no: string | null;
} {
  switch (row.source_type) {
    case 'inventory_adjustment':
      return {
        source_label: 'Inventory Adjustment',
        reference_no: row.adjustment_number ?? row.source_id,
      };
    case 'delivery_order':
      return {
        source_label: 'Delivery Order',
        reference_no: row.do_number ?? row.supplier_invoice_number ?? row.source_id,
      };
    case 'pharmacy_dispense':
      return {
        source_label: 'Pharmacy Dispense',
        reference_no: row.dispense_number ?? row.source_id,
      };
    case 'product_release':
      // PRD.206 — outbound stock movement from confirmed Product Release.
      return {
        source_label: 'Product Release',
        reference_no: row.release_number ?? row.source_id,
      };
    default:
      return {
        source_label: row.source_type ?? 'Unknown',
        reference_no: row.source_id,
      };
  }
}

/**
 * Compute a per-item cumulative running balance and return rows in the
 * caller's preferred display order (newest first by default).
 *
 * The cumulative sum is calculated against the chronological ordering
 * (movement_date, created_at, id) so it remains deterministic regardless
 * of how the caller sorts the input. Each row's `running_balance` is the
 * net balance for `inventory_item_id` immediately AFTER that movement.
 */
export function computeRunningBalances(
  rows: StockCardMovementRow[],
  displayOrder: 'asc' | 'desc' = 'desc',
): StockCardMovementVM[] {
  // 1) Sort ascending for cumulative calculation.
  const ascending = [...rows].sort(compareAscending);

  // 2) Walk forward, tracking per-item running balance.
  const balanceByItem = new Map<string, number>();
  const enriched: StockCardMovementVM[] = ascending.map((row) => {
    const itemKey = row.inventory_item_id ?? '__null__';
    const prior = balanceByItem.get(itemKey) ?? 0;
    const next = prior + (row.quantity_in ?? 0) - (row.quantity_out ?? 0);
    balanceByItem.set(itemKey, next);
    const { source_label, reference_no } = resolveSource(row);
    return {
      ...row,
      source_label,
      reference_no,
      running_balance: next,
    };
  });

  // 3) Sort to caller's display preference.
  if (displayOrder === 'desc') {
    enriched.reverse();
  }
  return enriched;
}

function compareAscending(a: StockCardMovementRow, b: StockCardMovementRow): number {
  const ad = a.movement_date ?? '';
  const bd = b.movement_date ?? '';
  if (ad !== bd) return ad < bd ? -1 : 1;
  const ac = a.created_at ?? '';
  const bc = b.created_at ?? '';
  if (ac !== bc) return ac < bc ? -1 : 1;
  return a.movement_id < b.movement_id ? -1 : a.movement_id > b.movement_id ? 1 : 0;
}
